import React, { useEffect } from 'react';
import { CheckCircle, XCircle, AlertCircle, X } from 'lucide-react';

/**
 * Toast Notification Component
 * Displays success, error, or info messages
 *
 * @param {string} message - Message to display
 * @param {string} type - Toast type: 'success', 'error', 'info', 'warning'
 * @param {number} duration - Auto-dismiss duration in ms (0 = no auto-dismiss)
 * @param {function} onClose - Callback when toast is closed
 * @param {boolean} show - Whether to show the toast
 */
const Toast = ({ message, type = 'info', duration = 3000, onClose, show = true }) => {
    useEffect(() => {
        if (show && duration > 0) {
            const timer = setTimeout(() => {
                if (onClose) onClose();
            }, duration);

            return () => clearTimeout(timer);
        }
    }, [show, duration, onClose]);

    if (!show) return null;

    // Icon and color based on type
    const config = {
        success: {
            icon: CheckCircle,
            bgColor: 'bg-green-50',
            borderColor: 'border-green-500',
            textColor: 'text-green-800',
            iconColor: 'text-green-500'
        },
        error: {
            icon: XCircle,
            bgColor: 'bg-red-50',
            borderColor: 'border-red-500',
            textColor: 'text-red-800',
            iconColor: 'text-red-500'
        },
        warning: {
            icon: AlertCircle,
            bgColor: 'bg-yellow-50',
            borderColor: 'border-yellow-500',
            textColor: 'text-yellow-800',
            iconColor: 'text-yellow-500'
        },
        info: {
            icon: AlertCircle,
            bgColor: 'bg-blue-50',
            borderColor: 'border-blue-500',
            textColor: 'text-blue-800',
            iconColor: 'text-blue-500'
        }
    };

    const { icon: Icon, bgColor, borderColor, textColor, iconColor } = config[type] || config.info;

    return (
        <div className="fixed top-4 right-4 z-50 animate-slide-in-right">
            <div
                className={`flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg border-l-4 ${bgColor} ${borderColor} max-w-md`}
            >
                <Icon className={iconColor} size={24} />
                <p className={`flex-1 text-sm font-medium ${textColor}`}>{message}</p>
                {onClose && (
                    <button
                        onClick={onClose}
                        className={`flex-shrink-0 ${textColor} hover:opacity-75 transition-opacity`}
                    >
                        <X size={18} />
                    </button>
                )}
            </div>
        </div>
    );
};

/**
 * Toast Container Component
 * Manages multiple toast notifications
 */
export const ToastContainer = ({ toasts, removeToast }) => {
    return (
        <div className="fixed top-4 right-4 z-50 space-y-2">
            {toasts.map((toast) => (
                <Toast
                    key={toast.id}
                    message={toast.message}
                    type={toast.type}
                    duration={toast.duration}
                    onClose={() => removeToast(toast.id)}
                    show={true}
                />
            ))}
        </div>
    );
};

/**
 * Custom hook for managing toasts
 * Usage:
 *   const { toasts, showToast, removeToast } = useToast();
 *   showToast('Success!', 'success');
 */
let toastCounter = 0; // Counter to ensure unique IDs

export const useToast = () => {
    const [toasts, setToasts] = React.useState([]);

    const showToast = (message, type = 'info', duration = 3000) => {
        // Generate unique ID using timestamp + counter to avoid duplicates
        const id = `${Date.now()}-${++toastCounter}`;
        const newToast = { id, message, type, duration };

        setToasts((prev) => [...prev, newToast]);

        // Auto-remove after duration
        if (duration > 0) {
            setTimeout(() => {
                removeToast(id);
            }, duration);
        }
    };

    const removeToast = (id) => {
        setToasts((prev) => prev.filter((toast) => toast.id !== id));
    };

    return { toasts, showToast, removeToast };
};

export default Toast;
